import * as _ from 'lodash-es';
import React from 'react';
import { useLazyQuery } from '@apollo/client';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type {
  TopicPostsOrder,
  TopicPostsType,
} from '../../graphql/generated/graphql';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import TopicPostSection from './TopicPostSection';
import usePinMutations from './usePinMutations';

export const SUBTOPIC_POSTS_INITIAL_PAGE_SIZE = 5;

const SUBTOPIC_POSTS_SECTION_TOPIC_FRAGMENT = gql(`
  fragment SubtopicPostsSection_topic on Topic {
    id
    posts(
      after: $postsAfterCursor
      first: $postsFirst
      orderBy: $orderBy
      type: $type
    ) {
      ...TopicPostSection_posts
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const SUBTOPIC_POSTS_QUERY = gql(/** @complexity 11 */ `
  query subtopicPosts(
    $id: ID!
    $postsAfterCursor: String
    $postsFirst: Int!
    $orderBy: TopicPostsOrder!
    $type: TopicPostsType
  ) {
    topic(id: $id) {
      ...SubtopicPostsSection_topic
    }
  }
`);

interface SubtopicPostsSectionProps
  extends Pick<
    React.ComponentProps<typeof TopicPostSection>,
    'loading' | 'refetch'
  > {
  header: React.ReactNode;
  orderBy: TopicPostsOrder;
  type: TopicPostsType | null;
  topic: DocumentType<typeof SUBTOPIC_POSTS_SECTION_TOPIC_FRAGMENT>;
  main: boolean;
}

const SubtopicPostsSection = ({
  orderBy,
  type,
  topic,
  header,
  ...props
}: SubtopicPostsSectionProps) => {
  const initialPosts = topic.posts;
  const [loadMore, { called, data, ...args }] = useLazyQuery(
    SUBTOPIC_POSTS_QUERY,
    {
      variables: {
        id: topic.id,
        postsFirst: SUBTOPIC_POSTS_INITIAL_PAGE_SIZE * 2,
        orderBy,
        type,
      },
    },
  );

  const { pinPost, unpinPost } = usePinMutations({
    topicId: topic.id,
    type,
    orderBy,
    postsCount: _.compact(topic.posts?.edges).length,
  });

  let fetchMore = null;

  if (called) {
    fetchMore = ConnectionHelper.generateFetchMore(
      SUBTOPIC_POSTS_QUERY,
      { data, ...args },
      'topic.posts',
      { afterCursorVariableKey: 'postsAfterCursor' },
    );
  } else if (initialPosts?.pageInfo.hasNextPage) {
    fetchMore = loadMore;
  }

  const posts = data?.topic.posts || initialPosts;

  if (posts == null || posts.edges?.length === 0) return null;

  return (
    <TopicPostSection
      {...props}
      orderBy={orderBy}
      fetchMore={fetchMore}
      header={header}
      posts={posts}
      pinPost={pinPost}
      unpinPost={unpinPost}
      topicId={topic.id}
    />
  );
};

export default SubtopicPostsSection;
